import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import {
  IFormsProvider,
  KeyValuePair,
  LFOfflineOrder,
  LFOfflineOrderSourceType,
  LiveForm,
  ResourceData,
  ResourceState,
  SAVE_FORM_RESULT
} from '@lfw/roesberg.liveforms.core';
import {
  catchError,
  concatMap,
  defaultIfEmpty,
  EMPTY,
  filter,
  firstValueFrom,
  from,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  tap,
  toArray,
  withLatestFrom
} from 'rxjs';
import { NetworkService } from '../../network.service';
import { RETRY_RESULTS, RetryDialogComponent } from '../../shared/dialogs/retry-dialog/retry-dialog.component';
import { ResourceHelper } from '../../shared/helpers/resource-helper';
import { OfflineFormsClient } from '../offline-orders/offline-forms.client';
import { OnlineFormsClient } from './online-forms.client';

@Injectable()
export class OnlineFormsProvider implements IFormsProvider<LiveForm> {
  private readonly route = inject(ActivatedRoute);
  private readonly offlineDbClient = inject(OfflineFormsClient);
  private readonly dialog = inject(MatDialog);
  private readonly onlineFormsClient = inject(OnlineFormsClient);

  #networkService = inject(NetworkService);
  uploaded: KeyValuePair[] = [];
  saveQueue = new Subject<Observable<ResourceData>>();
  cachedResourceBlobs = new Map<string, Blob>();

  constructor() {
    this.saveQueue.pipe(concatMap((save) => save)).subscribe((resource: ResourceData) => {
      this.uploaded.push({ key: resource.saveId, value: resource.fileId });
    });
  }

  deleteOfflineFormResourceByFileId(fileId: string): void {
    // Not needed for online forms as resources are deleted during form save
    return;
  }

  saveForm(form: LiveForm, message?: string): Observable<SAVE_FORM_RESULT> {
    const resourceData = form.resourceData;
    const orderId = form.order.id;
    const toDelete = ResourceHelper.filterResourcesByOrderAndState(resourceData, [ResourceState.Deleted], orderId).map(
      (resource) => resource.fileId
    );

    return of(form).pipe(
      withLatestFrom(this.#networkService.state$),
      switchMap(([_, networkState]) => {
        if (networkState === 'offline') {
          return this.saveFormOffline(form, message);
        }

        const pendingUploads = ResourceHelper.filterResourcesByOrderAndState(
          resourceData,
          [ResourceState.New, ResourceState.PendingUpload],
          orderId
        );
        const uploads$ = this.performUploads(pendingUploads);

        return uploads$.pipe(
          switchMap(() => this.onlineFormsClient.saveForm(form, toDelete, this.uploaded)),
          switchMap((result) =>
            this.cleanupOfflineOrderAfterOnlineSave(form.id).pipe(
              defaultIfEmpty(null),
              map(() => result)
            )
          )
        );
      })
    );
  }

  private performUploads(pendingUploads: ResourceData[]): Observable<ResourceData[]> {
    if (!pendingUploads || pendingUploads.length === 0) {
      return of([] as ResourceData[]);
    }

    return this.getRequiredRouteParams().pipe(
      switchMap((params: { form: string; project: string }) =>
        from(pendingUploads).pipe(
          concatMap((resource: ResourceData) => {
            const blob = resource.data ?? this.cachedResourceBlobs.get(resource.fileId);
            return from(this.onlineFormsClient.uploadResource(resource, params.project, params.form, blob)).pipe(
              tap((uploadedResource: ResourceData) => this.processUploadedResource(uploadedResource, resource)),
              map((uploadedResource: ResourceData) => [uploadedResource]),
              catchError((err) => {
                console.warn('Resource upload failed', err);
                return of([] as ResourceData[]);
              })
            );
          }),
          toArray(),
          map((results) => results.flat())
        )
      )
    );
  }

  private processUploadedResource(uploadedResource: ResourceData, resource: ResourceData): void {
    resource.fileId = uploadedResource.fileId ?? resource.fileId;
    resource.saveId = uploadedResource.saveId ?? resource.saveId;
    resource.state = ResourceState.Unchanged;

    const existingIndex = this.uploaded.findIndex(
      (x) => (uploadedResource.saveId && x.key === uploadedResource.saveId) || x.value === uploadedResource.fileId
    );

    const entry: KeyValuePair = {
      key: uploadedResource.saveId ?? resource.saveId,
      value: uploadedResource.fileId ?? resource.fileId
    };

    if (existingIndex === -1) {
      this.uploaded.push(entry);
    } else {
      this.uploaded[existingIndex] = entry;
    }
  }

  private cleanupOfflineOrderAfterOnlineSave(formId: string): Observable<void> {
    return from(this.offlineDbClient.getOfflineOrderById(formId)).pipe(
      map((offlineOrder) => (offlineOrder instanceof LFOfflineOrder ? offlineOrder.id : undefined)),
      filter((orderId: string | undefined): orderId is string => !!orderId),
      switchMap((orderId: string) => this.offlineDbClient.loadResourcesByOrderId(orderId)),
      switchMap(async (resources: ResourceData[]) => {
        await Promise.allSettled(
          resources.map((resource: ResourceData) => this.offlineDbClient.deleteResourceByFileId(resource.fileId))
        );
      }),
      switchMap((_) => this.offlineDbClient.removeOfflineOrderFromDb(formId)),
      catchError((err) => {
        console.warn(err);
        return EMPTY;
      })
    );
  }

  async saveFormResource(data: ResourceData): Promise<void> {
    const params = await firstValueFrom(this.getRequiredRouteParams());
    this.saveQueue.next(this.upload(params.project, params.form, data));
  }

  private getRequiredRouteParams(): Observable<{ form: string; project: string }> {
    return this.route.paramMap.pipe(
      map((params) => ({ form: params.get('form'), project: params.get('project') })),
      filter((params): params is { form: string; project: string } => !!params.form && !!params.project),
      take(1)
    );
  }

  private upload(projectId: string, formId: string, data: ResourceData): Observable<ResourceData> {
    return of(data).pipe(
      withLatestFrom(this.#networkService.state$),
      switchMap(async ([_, networkState]) => {
        if (networkState === 'offline') {
          return await this.uploadResourceOffline(data);
        } else {
          return this.onlineFormsClient.uploadResource(data, projectId, formId, data.data);
        }
      })
    );
  }

  loadFormResource(fileId: string, formId?: string, projectId?: string): Observable<Blob> {
    return this.route.paramMap.pipe(
      map((params) => ({ form: formId ?? params.get('form'), project: projectId ?? params.get('project') })),
      filter((params): params is { form: string; project: string } => !!params.form && !!params.project),
      switchMap((params) => this.onlineFormsClient.loadResource(params, fileId)),
      tap((blob) => this.cachedResourceBlobs.set(fileId, blob)),
      take(1)
    );
  }

  loadForm(formId?: string): Observable<LiveForm> {
    return this.route.paramMap.pipe(
      map((params) => params.get('form') || formId),
      filter((formId: string | undefined): formId is string => !!formId),
      switchMap((formId) => this.onlineFormsClient.loadForm(formId)),
      tap(() => this.cachedResourceBlobs.clear()),
      take(1)
    );
  }

  loadFormForEdit(force: boolean): Observable<LiveForm> {
    return this.route.paramMap.pipe(
      map((params) => params.get('form')),
      filter((formId: string | null): formId is string => !!formId),
      switchMap((formId) => this.onlineFormsClient.loadFormForEdit(formId, force)),
      tap(() => this.cachedResourceBlobs.clear()),
      take(1)
    );
  }

  private saveFormOffline(form: LiveForm, message?: string): Observable<SAVE_FORM_RESULT> {
    return this.dialog
      .open(RetryDialogComponent, { data: { message } })
      .afterClosed()
      .pipe(
        switchMap(async (result) => {
          if (result === RETRY_RESULTS.RETRY) {
            return firstValueFrom(this.saveForm(form, message));
          } else if (result === RETRY_RESULTS.STORE_OFFLINE) {
            for (const resource of form.attachedResources) {
              const data = this.cachedResourceBlobs.get(resource.fileId);
              if (data) {
                await firstValueFrom(
                  this.offlineDbClient.saveResource({
                    ...resource,
                    state: ResourceState.Unchanged,
                    data,
                    fileId: resource.fileId,
                    saveId: '',
                    orderId: form.order.id
                  })
                );
              }
            }
            for (const resource of form.resourceData) {
              if (!resource.data) {
                resource.data = this.cachedResourceBlobs.get(resource.fileId) || resource.data;
              }
              if (!resource.data) {
                continue;
              }
              if (resource.state === ResourceState.New) {
                await firstValueFrom(
                  this.offlineDbClient.saveResource({ ...resource, state: ResourceState.PendingUpload })
                );
              } else {
                await firstValueFrom(this.offlineDbClient.saveResource(resource));
              }
            }
            await this.offlineDbClient.saveOfflineOrder(form, LFOfflineOrderSourceType.FAILED_ONLINE_SAVE, true);
            return SAVE_FORM_RESULT.SAVED_OFFLINE;
          } else {
            return SAVE_FORM_RESULT.FAILED;
          }
        })
      );
  }

  private async uploadResourceOffline(data: ResourceData): Promise<ResourceData> {
    const buffer = await data.data.arrayBuffer();
    data.data = new Blob([buffer], { type: data.data.type });
    return firstValueFrom(this.offlineDbClient.saveResource({ ...data, state: ResourceState.PendingUpload }));
  }
}
